/**
 * assets/cart-add-button.js
 *
 * Powers <cart-add-button data-variant-id="..." data-product-id="..."> used
 * in snippets/card-product.liquid.
 *
 * On click:
 *   1. POSTs to /cart/add.js to add the variant (qty 1)
 *   2. Re-fetches sections/cart-drawer.liquid via ?section_id=cart-drawer
 *      and swaps the updated <cart-drawer> markup into the page
 *   3. Opens the cart drawer
 *
 * Requires: sections/cart-drawer.liquid section id = "cart-drawer"
 * (confirm this matches how it's included in theme.liquid, e.g. {% section 'cart-drawer' %}).
 */
(function () {
  if (customElements.get('cart-add-button')) return;

  const routesRoot =
    (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  customElements.define(
    'cart-add-button',
    class CartAddButton extends HTMLElement {
      constructor() {
        super();
        this.button = this.querySelector('button');
        this.spinner = this.querySelector('.loading-overlay__spinner');
        if (this.button) {
          this.onClickHandler = this.onClick.bind(this);
          this.button.addEventListener('click', this.onClickHandler);
        }
      }

      get variantId() {
        return this.dataset.variantId;
      }

      onClick(event) {
        event.preventDefault();

        if (this.button.getAttribute('aria-disabled') === 'true') return;

        if (!this.variantId) {
          console.error('cart-add-button: missing data-variant-id attribute');
          return;
        }

        this.setLoading(true);

        fetch(`${routesRoot}cart/add.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({
            id: this.variantId,
            quantity: 1,
          }),
        })
          .then((response) => response.json())
          .then((data) => {
            if (data.status) {
              // Shopify /cart/add.js returns { status, message, description } on failure
              this.showError(data.description || data.message || 'Could not add product to cart.');
              return null;
            }
            return this.refreshCartDrawer();
          })
          .catch((error) => {
            console.error('cart-add-button: add to cart request failed', error);
            this.showError('Something went wrong adding this to your cart. Please try again.');
          })
          .finally(() => {
            this.setLoading(false);
          });
      }

      refreshCartDrawer() {
        const url = `${window.location.pathname}?section_id=cart-drawer`;

        return fetch(url)
          .then((response) => {
            if (!response.ok) {
              throw new Error(`cart-drawer section fetch failed: ${response.status}`);
            }
            return response.text();
          })
          .then((responseText) => {
            const parsedHtml = new DOMParser().parseFromString(responseText, 'text/html');
            const newCartDrawer = parsedHtml.querySelector('cart-drawer');
            const existingCartDrawer = document.querySelector('cart-drawer');

            if (newCartDrawer && existingCartDrawer && existingCartDrawer.parentNode) {
              existingCartDrawer.replaceWith(newCartDrawer);
              this.openDrawer(document.querySelector('cart-drawer'));
            } else {
              console.error(
                'cart-add-button: could not find <cart-drawer> in the fetched section markup or on the page.'
              );
            }

            document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));
          });
      }

      openDrawer(cartDrawer) {
        if (!cartDrawer) return;

        // Prefer the theme's own open() method if the <cart-drawer> element defines one.
        if (typeof cartDrawer.open === 'function') {
          cartDrawer.open();
          return;
        }

        // Fallback: toggle common drawer-open classes/attrs directly.
        cartDrawer.classList.remove('is-empty');
        cartDrawer.classList.add('active', 'animate');
        document.documentElement.classList.add('overflow-hidden');

        const overlay = cartDrawer.querySelector('.drawer__overlay, .cart-drawer__overlay');
        if (overlay) {
          overlay.addEventListener('click', () => this.closeDrawer(cartDrawer), { once: true });
        }
      }

      closeDrawer(cartDrawer) {
        if (typeof cartDrawer.close === 'function') {
          cartDrawer.close();
          return;
        }
        cartDrawer.classList.remove('active', 'animate');
        document.documentElement.classList.remove('overflow-hidden');
      }

      setLoading(isLoading) {
        if (!this.button) return;
        this.button.toggleAttribute('aria-disabled', isLoading);
        this.button.classList.toggle('loading', isLoading);
        if (this.spinner) this.spinner.classList.toggle('hidden', !isLoading);
      }

      showError(message) {
        console.error('cart-add-button:', message);
        // Hook this into your theme's existing toast/notification system if it has one, e.g.:
        // if (window.theme && typeof window.theme.showToast === 'function') {
        //   window.theme.showToast(message, 'error');
        //   return;
        // }
        alert(message);
      }
    }
  );
})();